// Heuristic parser that turns raw OCR text off a photographed business card
// into pre-filled form fields. OCR line order/casing is unreliable, so this
// only extracts the fields we can match with confidence (email/phone/website)
// and best-effort guesses for the rest — the scan screen always lets the
// user review/edit before sending an invite.

export interface ParsedBusinessCard {
  businessName?: string;
  phone?: string;
  email?: string;
  website?: string;
  addressGuess?: string;
  zipGuess?: string;
  rawLines: string[];
}

const EMAIL_REGEX = /[a-z0-9.+_-]+@[a-z0-9-]+\.[a-z0-9.-]+/i;
const PHONE_REGEX = /(\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/;
const WEBSITE_REGEX = /\b(?:https?:\/\/)?(?:www\.)?[a-z0-9-]+\.(?:com|net|org|io|co|biz|info|us)(?:\/[^\s]*)?\b/i;
const ADDRESS_HINT_REGEX = /\b\d{1,6}\s+\w+.*\b(st|street|ave|avenue|rd|road|blvd|boulevard|suite|ste|dr|drive|way|ln|lane)\b/i;
const ZIP_REGEX = /\b\d{5}(?:-\d{4})?\b/;

function normalizePhone(match: string): string {
  const digits = match.replace(/\D/g, '');
  const tenDigits = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (tenDigits.length !== 10) return match.trim();
  return `(${tenDigits.slice(0, 3)}) ${tenDigits.slice(3, 6)}-${tenDigits.slice(6)}`;
}

function normalizeWebsite(match: string): string {
  const trimmed = match.trim().replace(/[),.]+$/, '');
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

export function parseBusinessCardText(rawText: string): ParsedBusinessCard {
  const rawLines = rawText
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean);

  const emailMatch = rawText.match(EMAIL_REGEX);
  const email = emailMatch ? emailMatch[0].toLowerCase() : undefined;

  const phoneMatch = rawText.match(PHONE_REGEX);
  const phone = phoneMatch ? normalizePhone(phoneMatch[0]) : undefined;

  // A website match that sits inside the email address (the domain half)
  // isn't a separate website — skip it.
  const websiteMatch = rawLines
    .map(line => line.match(WEBSITE_REGEX))
    .find(m => m && (!email || !email.includes(m[0].toLowerCase())));
  const website = websiteMatch ? normalizeWebsite(websiteMatch[0]) : undefined;

  const addressLine = rawLines.find(line => ADDRESS_HINT_REGEX.test(line));
  const zipSearchText = addressLine ?? rawLines.filter(line => line !== phoneMatch?.[0]).join(' ');
  const zipMatch = zipSearchText.match(ZIP_REGEX);
  const zipGuess = zipMatch ? zipMatch[0] : undefined;

  const leftoverLines = rawLines.filter(line => {
    if (email && line.toLowerCase().includes(email)) return false;
    if (phone && line.replace(/\D/g, '').includes(phone.replace(/\D/g, ''))) return false;
    if (website && WEBSITE_REGEX.test(line)) return false;
    if (addressLine && line === addressLine) return false;
    return true;
  });

  // Best guess for the business name: the longest remaining line tends to be
  // the company name rather than a person's name or a job title, which are
  // usually shorter on printed cards.
  const businessName = leftoverLines.length
    ? leftoverLines.reduce((longest, line) => (line.length > longest.length ? line : longest))
    : undefined;

  return {
    businessName,
    phone,
    email,
    website,
    addressGuess: addressLine,
    zipGuess,
    rawLines,
  };
}
